/** Returns the correct unit based on needs. 
 * - For backend concerns, 'Metric' becomes 'meter'
 * - For frontend concerns 'meter' becomes 'm'
 * 
 * @param {string} measurementType 
 * @returns {string} Value of key-value pair of measurement association.
 */

export function returnUnit(measurementType) {
    const measurements = {
        'Metric': 'meter',
        'Imperial': 'feet',
        'meter': 'm',
        'feet': 'ft',
    }

    return measurements[measurementType]
}

/** This function takes in meters or feet, and applies conversion when appropriate. 
 * - Note: It rounds the value to the nearest _second_ decimal place.
 * 
 * @param {number} unit The measurement units to convert.
 * @param {'Metric' | 'Imperial'} unitType Type of unit passed in through {**unit**} argument
 * @param {'Metric' | 'Imperial'} unitSystem Current project unit system
 * @returns {number} Converted measurement
 */

export function convertUnits(unit, unitType, unitSystem) {
    if (unitType === 'Metric' && unitSystem === 'Metric') {
        return unit
    }
    else if (unitType === 'Metric' && unitSystem === 'Imperial') {
        // convert meters to feet
        const valueInFeet = unit * 3.28
        const roundedFeet = Number(valueInFeet.toFixed(2))
        return roundedFeet
    }
    else if (unitType === 'Imperial' && unitSystem === 'Metric') {
        // convert feet to meters
        const valueInMeters = unit / 3.28
        const roundedMeters = Number(valueInMeters.toFixed(2))
        return roundedMeters
    }
    // unitType must be 'Imperial' and selectedMeasurementSystem must be 'Imperial', so just return
    else {
        return unit
    }
}

/**
 * Removes branching code from the <img> tag to serve the correct image on the kiln card
 * @param {"Square" | "Rectangle" | "Polygon"} variant
 * @param {"Square" | "Rectangle" | "Pentagon" | "Hexagon" | "Heptagon" | "Octagon"} shape from kilnCardObject.shape
 * @returns {string} link to correct public image
 */

export function getKilnImage(variant, shape) {
    if (variant === 'Rectangle' && shape === 'Rectangle') {
        return '/rectangle-kiln.png'
    }
    else if (variant === 'Polygon' && shape.includes('gon')) {
        const polygonImage = {
            'Pentagon': '/pentagon-kiln.png',
            'Hexagon': '/hexagon-kiln.png',
            'Heptagon': '/heptagon-kiln.png',
            'Octagon': '/octagon-kiln.png',
        }
        return polygonImage[shape]
    }
    else return '/square-kiln.png'
}

/**
 * Component to normalize the styling of volumetric units within the KilnList.js table
 * @param {Object} props 
 * @param {number} props.unit The unit value to display
 * @param {"squared" | "cubed"} props.volumeType Adds the correct exponent to the unit
 * @returns {JSX.Element} Component that wraps the {unit} value along with the correct exponent
 */

export function VolumetricUnit({ unit, volumeType }) {
    const volumeRelationship = {
        'squared': 2,
        'cubed': 3
    }
    return (
        <>
            {` ${returnUnit(unit)}`}
            <sup>{volumeRelationship[volumeType]}</sup>
        </>
    )
}

/**
 * Component to normalize the units displayed in the KilnList.js table
 * @param {Object} props 
 * @param {number} props.unit
 * @returns {JSX.Element}
 */

export function Unit({ unit }) {
    const correctUnit = returnUnit(unit)
    return (
        <>
            {` ${correctUnit}`}
        </>
    )
}